<template>
  <div>
    <Line :chart-data="chartData" :chart-options="chartOptions" />
  </div>
</template>

<script>
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale
);

export default {
  name: "HealthSleepChart",
  components: { Line },
  props: {
    items: { type: Array, default: () => [] },
  },
  setup(props) {
    const sorted = computed(() =>
      [...(props.items || [])].sort((a, b) =>
        String(a.calendar_date).localeCompare(String(b.calendar_date))
      )
    );

    const toHours = (seconds) => {
      if (seconds == null) return null;
      const v = Number(seconds);
      if (Number.isNaN(v)) return null;
      return v / 3600.0;
    };

    const chartData = computed(() => {
      const rows = sorted.value;

      return {
        labels: rows.map((e) => e.calendar_date),
        datasets: [
          // Sleep score (0..100)
          {
            label: "Sleep Score",
            data: rows.map((e) => (e.sleep_score == null ? null : Number(e.sleep_score))),
            yAxisID: "yScore",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#42A5F5",
            backgroundColor: "rgba(66,165,245,0.15)",
            borderWidth: 1,
            pointRadius: 0,
          },

          // Sleep durations (hours)
          {
            label: "Total Sleep (h)",
            data: rows.map((e) => toHours(e.sleep_time_seconds)),
            yAxisID: "yHours",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#81C784",
            backgroundColor: "rgba(129,199,132,0.15)",
            borderWidth: 1,
            pointRadius: 0,
          },
          {
            label: "Deep (h)",
            data: rows.map((e) => toHours(e.deep_sleep_seconds)),
            yAxisID: "yHours",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#AB47BC",
            backgroundColor: "rgba(171,71,188,0.15)",
            borderWidth: 1,
            pointRadius: 0,
          },
          {
            label: "Light (h)",
            data: rows.map((e) => toHours(e.light_sleep_seconds)),
            yAxisID: "yHours",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#FFB74D",
            backgroundColor: "rgba(255,183,77,0.15)",
            borderWidth: 1,
            pointRadius: 0,
          },
          {
            label: "REM (h)",
            data: rows.map((e) => toHours(e.rem_sleep_seconds)),
            yAxisID: "yHours",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#4FC3F7",
            backgroundColor: "rgba(79,195,247,0.15)",
            borderWidth: 1,
            pointRadius: 0,
          },
          {
            label: "Awake (h)",
            data: rows.map((e) => toHours(e.awake_sleep_seconds)),
            yAxisID: "yHours",
            tension: 0.3,
            spanGaps: true,
            borderColor: "#EF5350",
            backgroundColor: "rgba(239,83,80,0.15)",
            borderWidth: 1,
            pointRadius: 0,
          },
        ],
      };
    });

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { position: "top" },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const name = ctx.dataset.label || "";
              const v = ctx.parsed.y;
              if (v == null) return `${name}: -`;
              if (name.includes("(h)")) return `${name}: ${v.toFixed(2)} h`;
              if (name.includes("Score")) return `${name}: ${Math.round(v)}`;
              return `${name}: ${v}`;
            },
          },
        },
      },
      scales: {
        x: { title: { display: true, text: "Date" } },

        yScore: {
          type: "linear",
          position: "left",
          title: { display: true, text: "Sleep Score" },
          suggestedMin: 0,
          suggestedMax: 100,
        },

        yHours: {
          type: "linear",
          position: "right",
          title: { display: true, text: "Hours" },
          grid: { drawOnChartArea: false },
        },
      },
    };

    return { chartData, chartOptions };
  },
};
</script>
